import { createSignal, createEffect, onCleanup, onMount, For, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { createStore, reconcile } from 'solid-js/store';
import { useApi, isOk } from '../api';
import BotStatusBadge, { type BotState } from './BotStatusBadge';
import Text from './Text';
import { BOT_ACTIONS, botAccessLabelKey, can } from '../services/resourceActions';
import { useAnchoredMenu } from '../hooks/useAnchoredMenu';
import styles from './BotSelector.module.css';

export interface BotRegistryEntry {
    id: string;
    minecraftUsername: string;
    minecraftUuid: string;
    isOwner: boolean;
    myActions: string[];
    state?: BotState;
    server?: string | null;
}

interface BotSelectorProps {
    storageKey: string;
    onSelect: (botId: string | null, bot: BotRegistryEntry | null) => void;
    adminAll?: boolean;
    forceBotId?: string;
    /** Preselected when the dialog has no stored choice yet, e.g. the navbar's active bot. */
    defaultId?: string;
    /** When provided, only bots whose id is in this list are shown/selectable (e.g. bots assigned to a storage). */
    allowedIds?: string[];
}

export default function BotSelector(props: BotSelectorProps) {
    const api = useApi();
    const [bots, setBots] = createStore<BotRegistryEntry[]>([]);
    const initialId = props.forceBotId ?? localStorage.getItem(props.storageKey) ?? props.defaultId ?? '';
    const [selectedId, setSelectedId] = createSignal<string>(initialId);
    const menu = useAnchoredMenu(280);
    const { open, setOpen } = menu;

    const selectedBot = () => bots.find(b => b.id === selectedId()) ?? null;

    const fetchBots = async () => {
        const res = props.adminAll
            ? await api.botRegistry.listBots({ all: true })
            : await api.botRegistry.listBots();
        if (isOk(res.data)) {
            const allowed = props.allowedIds;
            const list = (res.data.value.bots as unknown as BotRegistryEntry[])
                .filter(b => !allowed || allowed.includes(b.id))
                .sort((a, b) => {
                    if (a.isOwner !== b.isOwner) return a.isOwner ? -1 : 1;
                    return a.minecraftUsername.localeCompare(b.minecraftUsername);
                });
            setBots(reconcile(list, { key: 'id' }));
            const target = selectedId();
            if (target && list.find(b => b.id === target)) {
                props.onSelect(target, list.find(b => b.id === target) ?? null);
            } else if (list.length > 0) {
                select(list[0].id);
            } else {
                setSelectedId('');
                props.onSelect(null, null);
            }
            if (open()) queueMicrotask(menu.reposition);
        }
    };

    const select = (id: string) => {
        setSelectedId(id);
        setOpen(false);
        localStorage.setItem(props.storageKey, id);
        props.onSelect(id, bots.find(b => b.id === id) ?? null);
    };

    const accessColor = (actions: string[]) =>
        can(actions, BOT_ACTIONS.AccessGrant) ? 'var(--error-500)'
            : can(actions, BOT_ACTIONS.Control) ? 'var(--primary-400)'
            : 'var(--dark-text-muted)';

    const BOT_STATE_POLL_MS = 5000;

    createEffect(() => {
        props.allowedIds;
        void fetchBots();
    });

    onMount(() => {
        const handle = window.setInterval(() => { void fetchBots(); }, BOT_STATE_POLL_MS);
        onCleanup(() => clearInterval(handle));
    });

    return (
        <div class={styles.container} ref={menu.setContainerRef}>
            <Show when={bots.length === 0 && props.allowedIds}>
                <div class={styles.empty}>
                    <i class="fa-solid fa-robot" />
                    <span><Text val="BOTSELECTOR_NO_ASSIGNED_BOTS" /></span>
                </div>
            </Show>
            <Show when={bots.length === 0 && !props.allowedIds}>
                <div class={styles.empty}>
                    <i class="fa-solid fa-robot" />
                    <span><Text val="BOTSELECTOR_NO_BOTS" /> <a href="/bots" class={styles.link}><Text val="BOTSELECTOR_LINK_ONE" /></a></span>
                </div>
            </Show>

            <Show when={bots.length > 0}>
                <button
                    type="button"
                    ref={menu.setTriggerRef}
                    class={`${styles.trigger} ${open() ? styles.triggerOpen : ''}`}
                    onClick={menu.toggle}
                >
                    <Show when={selectedBot()} fallback={<span class={styles.placeholder}><Text val="BOTSELECTOR_PLACEHOLDER" /></span>}>
                        {(bot) => (
                            <>
                                <img
                                    class={styles.avatar}
                                    src={`https://mc-heads.net/avatar/${bot().minecraftUuid}/24`}
                                    alt=""
                                />
                                <span class={styles.name}>{bot().minecraftUsername}</span>
                                <BotStatusBadge state={bot().state} />
                                <Show when={bot().isOwner}>
                                    <span class={styles.ownerBadge}><Text val="BOTSELECTOR_OWNER" /></span>
                                </Show>
                                <Show when={!bot().isOwner}>
                                    <span class={styles.levelBadge} style={{ background: accessColor(bot().myActions) }}>
                                        <Text val={botAccessLabelKey(bot().myActions)} />
                                    </span>
                                </Show>
                            </>
                        )}
                    </Show>
                    <i class={`fa-solid fa-chevron-down ${styles.chevron} ${open() ? styles.chevronOpen : ''}`} />
                </button>

                <Show when={open()}>
                    <Portal mount={document.body}>
                    <div
                        ref={menu.setMenuRef}
                        class={styles.dropdown}
                        style={{
                            position: 'fixed',
                            left: `${menu.menuRect().left}px`,
                            top: `${menu.menuRect().top}px`,
                            width: `${menu.menuRect().width}px`,
                        }}
                    >
                        <For each={bots}>{(bot, index) => (
                            <button
                                type="button"
                                class={`${styles.option} ${bot.id === selectedId() ? styles.optionActive : ''}`}
                                style={{ '--i': index() } as any}
                                onClick={() => select(bot.id)}
                            >
                                <img
                                    class={styles.avatar}
                                    src={`https://mc-heads.net/avatar/${bot.minecraftUuid}/24`}
                                    alt=""
                                />
                                <div class={styles.optionInfo}>
                                    <span class={styles.optionName}>{bot.minecraftUsername}</span>
                                    <span class={styles.optionUuid}>{bot.minecraftUuid}</span>
                                </div>
                                <BotStatusBadge state={bot.state} />
                                <Show when={bot.isOwner}>
                                    <span class={styles.ownerBadge}><Text val="BOTSELECTOR_OWNER" /></span>
                                </Show>
                                <Show when={!bot.isOwner}>
                                    <span class={styles.levelBadge} style={{ background: accessColor(bot.myActions) }}>
                                        <Text val={botAccessLabelKey(bot.myActions)} />
                                    </span>
                                </Show>
                            </button>
                        )}</For>
                    </div>
                    </Portal>
                </Show>
            </Show>
        </div>
    );
}
